/**
 * UI Control Catalog
 *
 * Single source of truth for every ui_control key used in deal template fields.
 *
 * Two layers:
 *   1. Scalars  - each maps 1:1 to a platform.ui_primitive row.
 *   2. Composites - layouts that wire multiple primitives into a structured
 *                   table or list (e.g. checklist).
 *                   Defined here in TS for now. In a future phase, custom
 *                   composites will be stored in workspace.ui_control_composite
 *                   and workspace.ui_control_composite_slot tables.
 *
 * The `deal_template_field.ui_control` DB column CHECK constraint MUST mirror
 * the keys defined here. See packages/db/src/schema/sales/deal-template-field.ts.
 */
// ── Catalog ────────────────────────────────────────────────────────────────
export const UI_CONTROL_CATALOG = {
    // ── Text family ──────────────────────────────────────────────────────────
    text: {
        isComposite: false,
        primitiveKey: 'text',
        inputType: 'text_input',
        valueType: 'string',
    },
    textarea: {
        isComposite: false,
        primitiveKey: 'textarea',
        inputType: 'text_input',
        valueType: 'string',
    },
    rich_text: {
        isComposite: false,
        primitiveKey: 'rich_text',
        inputType: 'rich_text_editor',
        valueType: 'string',
    },
    email: {
        isComposite: false,
        primitiveKey: 'email',
        inputType: 'email_input',
        valueType: 'string',
    },
    phone: {
        isComposite: false,
        primitiveKey: 'phone',
        inputType: 'phone_input',
        valueType: 'string',
    },
    url: {
        isComposite: false,
        primitiveKey: 'url',
        inputType: 'url_input',
        valueType: 'string',
    },
    // ── Choice / selection family ────────────────────────────────────────────
    select: {
        isComposite: false,
        primitiveKey: 'select',
        inputType: 'dropdown',
        valueType: 'string',
    },
    multi_select: {
        isComposite: false,
        primitiveKey: 'multi_select',
        inputType: 'dropdown',
        valueType: 'array',
    },
    pill_radio: {
        isComposite: false,
        primitiveKey: 'pill_radio',
        inputType: 'radio',
        valueType: 'string',
    },
    pill_select: {
        isComposite: false,
        primitiveKey: 'pill_select',
        inputType: 'checkbox_group',
        valueType: 'array',
    },
    stack_option: {
        isComposite: false,
        primitiveKey: 'stack_option',
        inputType: 'radio',
        valueType: 'string',
    },
    tags: {
        isComposite: false,
        primitiveKey: 'tags',
        inputType: 'tag_input',
        valueType: 'array',
    },
    /**
     * user_select - single-value autocomplete whose options are sourced at
     * query-time from the active workspace members.
     *
     * Stored value: the selected user's UUID (string).
     * options JSONB stores a dataSource directive: `{ "dataSource": "workspace_members" }`
     */
    user_select: {
        isComposite: false,
        primitiveKey: 'user_select',
        inputType: 'autocomplete',
        valueType: 'string',
    },
    // ── Boolean ──────────────────────────────────────────────────────────────
    boolean: {
        isComposite: false,
        primitiveKey: 'boolean',
        inputType: 'toggle',
        valueType: 'boolean',
    },
    // ── Numeric family ────────────────────────────────────────────────────────
    number: {
        isComposite: false,
        primitiveKey: 'number',
        inputType: 'number_input',
        valueType: 'number',
    },
    currency: {
        isComposite: false,
        primitiveKey: 'currency',
        inputType: 'number_input',
        // Stored as a { amount, currency } object, not a bare number.
        valueType: 'object',
    },
    percentage: {
        isComposite: false,
        primitiveKey: 'percentage',
        inputType: 'number_input',
        valueType: 'number',
    },
    slider: {
        isComposite: false,
        primitiveKey: 'slider',
        inputType: 'number_input',
        valueType: 'number',
    },
    rating: {
        isComposite: false,
        primitiveKey: 'rating',
        inputType: 'rating',
        valueType: 'number',
    },
    // ── Date / time family ────────────────────────────────────────────────────
    date: {
        isComposite: false,
        primitiveKey: 'date',
        inputType: 'date_picker',
        valueType: 'date',
    },
    time: {
        isComposite: false,
        primitiveKey: 'time',
        inputType: 'time_picker',
        valueType: 'string',
    },
    datetime: {
        isComposite: false,
        primitiveKey: 'datetime',
        inputType: 'datetime_picker',
        valueType: 'date',
    },
    // ── Composites ────────────────────────────────────────────────────────────
    /**
     * checklist
     *
     * A table where each row (option.optionType = 'item') has:
     *   - an "Applies" checkbox (hardcoded, not overridable)
     *   - a single-select dropdown from 'choice' options
     *
     * valueShape: Record<string, { applies: boolean | null; selection: string | null }>
     */
    checklist: {
        isComposite: true,
        layout: 'table',
        valueShape: 'Record<string, { applies: boolean | null; selection: string | null }>',
        slots: [
            {
                slotKey: 'item',
                label: 'Item',
                primitiveKey: 'text',
                overridable: true,
            },
            {
                slotKey: 'applies',
                label: 'Applies',
                primitiveKey: 'boolean',
                overridable: false,
            },
            {
                slotKey: 'choice',
                label: 'Selection',
                primitiveKey: 'select',
                overridable: true,
            },
        ],
        optionBuckets: [
            { bucketKey: 'item', label: 'Rows', role: 'row' },
            { bucketKey: 'choice', label: 'Dropdown choices', role: 'choice' },
        ],
    },
};
// ── Type guard ─────────────────────────────────────────────────────────────
/** Type guard: narrows an unknown string to a valid UiControlKey. */
export function isUiControlKey(value) {
    return typeof value === 'string' && value in UI_CONTROL_CATALOG;
}
// ── Helpers ────────────────────────────────────────────────────────────────
/**
 * Returns true when `key` maps to a composite control (multi-column/multi-slot layout).
 * Replaces the scattered inline string checks in DealPage and TrackedQuestionsPanel.
 */
export function isCompositeControl(key) {
    if (!isUiControlKey(key))
        return false;
    return UI_CONTROL_CATALOG[key].isComposite;
}
//# sourceMappingURL=ui-control-catalog.js.map